import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import { ApiError } from '../api/client';
import type { AIAction, CallOutcome, CallOutcomeCode, FollowUpSpec, LeadFocus, PolicyReason } from '../api/types';
import { ErrorMessage } from '../components/ErrorMessage';
import { GeoStatusBadge } from '../components/GeoStatusBadge';
import { Icon } from '../components/icons';
import { PageLoader } from '../components/Loader';
import { MutationError } from '../components/MutationError';
import { AddressConfirm } from '../ops/AddressConfirm';
import { ContactStop } from '../ops/ContactStop';
import { ConversationInsights } from '../ops/ConversationInsights';
import { LeadObjectsPanel } from '../objects/LeadObjectsPanel';
import { LeadContactEdit } from './leads/LeadContactEdit';
import { LeadNotes } from './leads/LeadNotes';
import { NotePreview } from './leads/NotePreview';
import { useLeadStatusLabels } from '../ops/leadStatus';
import { SameNumberLeads } from '../ops/SameNumberLeads';
import { type ObjectChoice, type OwnerObject, objectsPayload, reachedOutcomes, roleText } from '../owners/objectOutcomes';
import { OwnerObjectOutcomes } from '../owners/OwnerObjectOutcomes';
import { OwnersPanel } from '../owners/OwnersPanel';
import { AIReviewPanel } from '../ops/AIReviewPanel';
import { AppointmentForm, type AppointmentDefaults } from '../ops/AppointmentForm';
import { AppointmentSuccess } from '../ops/AppointmentSuccess';
import { AutoNext } from '../ops/AutoNext';
import { FollowUpPicker } from '../ops/FollowUpPicker';
import {
  appointmentStatusLabels,
  fmtDateTime,
  outcomeLabels,
  personName,
  terminalCallStatuses,
  tierLabels,
} from '../ops/format';
import { LeadTimeline } from '../ops/LeadTimeline';
import { NextCallCard, NextCallHint } from '../ops/NextCallCard';
import { useProject } from '../projects/context';
import { LiveAssistPanel } from '../ops/LiveAssistPanel';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { FollowUpsPage } from './FollowUpsPage';
import { usePhone } from '../telephony/phoneContext';
import type { BrowserPhoneState } from '../telephony/useBrowserPhone';
import { BuyerMatchHint } from '../ops/BuyerMatchHint';
import { CallObjectFacts } from '../objects/CallObjectFacts';
import { CallStatusBar } from '../ops/CallStatusBar';
import { UpcomingConfirmation } from '../ops/UpcomingConfirmation';
import { type WorkspacePhase, workflowIndex, workflowShortcuts, workflowSteps } from '../ops/workflow';
import { Shortcuts, WorkflowSteps } from '../ops/WorkflowSteps';

type Phase = WorkspacePhase;

const helpKey = 'em.akquise.hilfe';

const activeAppointmentStatuses = ['PLANNED', 'NEEDS_CONFIRMATION', 'CONFIRMED', 'RESCHEDULED'];

const quickOutcomes: { outcome: CallOutcomeCode; label: string; key?: string }[] = [
  { outcome: 'APPOINTMENT', label: 'Termin', key: 'T' },
  { outcome: 'CALLBACK', label: 'Rückruf', key: 'R' },
  { outcome: 'NOT_REACHED', label: 'Nicht erreicht' },
  { outcome: 'NOT_INTERESTED', label: 'Kein Interesse' },
  { outcome: 'WRONG_NUMBER', label: 'Falsche Nummer' },
  { outcome: 'WRONG_CONTACT', label: 'Falscher Ansprechpartner' },
  { outcome: 'CLARIFICATION', label: 'Klärung' },
  { outcome: 'BUYER', label: 'Sucht Immobilie' },
];

// Ergebnisse, die eine Notiz brauchen: was zu klären ist bzw. was der Kunde sucht (Kaufgesuch).
const noteRequired: Partial<Record<CallOutcomeCode, string>> = {
  CLARIFICATION: 'Bitte zuerst in der Notiz festhalten, was zu klären ist.',
  BUYER: 'Bitte zuerst in der Notiz festhalten, was der Kunde sucht (Objektart, Ort, Größe, Budget, Kapitalanlage oder selbst wohnen).',
};

const LOCK_HEARTBEAT_MS = 4 * 60_000;

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
}

// AcquisitionPage: operativer Arbeitsplatz der Telefonakquise. Statt Tabellen zeigt er genau den
// nächsten Lead, führt durch Anruf, Ergebnis und Folgeaktion und lädt den nächsten Lead vor.
// Ein Deep Link (/akquise/:leadId, z. B. aus einer Benachrichtigung) öffnet den Lead direkt.
export function AcquisitionPage() {
  const { leadId } = useParams();
  const [params, setParams] = useSearchParams();
  // ?start=1 (Startseite „Weiter zum Anruf“): den nächsten Lead sofort reservieren – einmalig, danach ohne Parameter.
  const [autoStart] = useState(() => params.get('start') === '1');
  useEffect(() => {
    if (params.has('start')) setParams((p) => (p.delete('start'), p), { replace: true });
  }, [params, setParams]);
  if (params.get('ansicht') === 'wiedervorlagen') return <MyFollowUps />;
  // ?anruf=<id>: angenommener Rückruf eines Kunden – der Arbeitsplatz zeigt direkt das laufende Gespräch.
  const callId = params.get('anruf') ?? undefined;
  return <Workspace key={`${leadId ?? 'queue'}:${callId ?? ''}`} initialLeadId={leadId} initialCallId={callId} autoStart={autoStart && !leadId && !callId} />;
}

// Meine Rückrufe und Wiedervorlagen – als Ansicht der Akquise statt eigener Menüpunkt. Fällige Einträge
// liefert die Akquise ohnehin zuerst aus; hier lassen sie sich vorab ansehen, öffnen oder erledigen.
function MyFollowUps() {
  const { project } = useProject();
  return (
    <div className="page workspace">
      <header className="page-header workspace-header">
        <h1>Akquise</h1>
        <div className="topbar-spacer" />
        <Link className="btn btn-primary" to={`/p/${project.key}/akquise`}>
          Zum Arbeitsplatz
        </Link>
      </header>
      <h2>Meine Rückrufe &amp; Wiedervorlagen</h2>
      <FollowUpsPage embedded defaultAssigned="me" />
    </div>
  );
}

function Workspace({ initialLeadId, initialCallId, autoStart = false }: { initialLeadId?: string; initialCallId?: string; autoStart?: boolean }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const [leadId, setLeadId] = useState<string | undefined>(initialLeadId);
  const [phase, setPhase] = useState<Phase>(initialCallId ? 'calling' : initialLeadId ? 'focus' : 'idle');
  const [tier, setTier] = useState<string | undefined>(initialLeadId ? 'MANUAL' : undefined);
  const [empty, setEmpty] = useState<{ reason?: string; next?: string | null }>();
  const [callId, setCallId] = useState<string | undefined>(initialCallId);
  const [note, setNote] = useState('');
  const [savedOutcome, setSavedOutcome] = useState<string>();
  const [aiAction, setAiAction] = useState<AIAction>();
  const [override, setOverride] = useState<{ reasons: PolicyReason[]; text: string }>();
  const [openedAt] = useState(() => Date.now());
  // Eigentümer (B-17): gewählte Nummer (Vorschlag aus der Arbeitsansicht), Ergebnisse für seine weiteren Objekte und ein
  // Termin für eines davon.
  const [target, setTarget] = useState<{ lead: string; phone: string }>();
  const [objectChoices, setObjectChoices] = useState<Record<string, ObjectChoice>>({});
  const [appointmentFor, setAppointmentFor] = useState<OwnerObject>();
  const [selectedBrokerId, setSelectedBrokerId] = useState<string>('');

  // Deep Link: Lead für diesen Mitarbeiter reservieren.
  useEffect(() => {
    if (initialLeadId) void papi.lockLead(initialLeadId).catch(() => undefined);
  }, [initialLeadId, papi]);

  const focusKey = projectKeys.focus(project.id, leadId ?? '');
  const focus = useQuery({
    queryKey: focusKey,
    queryFn: ({ signal }) => papi.focus(leadId as string, signal),
    enabled: !!leadId,
  });
  const stats = useQuery({
    queryKey: projectKeys.queueStats(project.id),
    queryFn: ({ signal }) => papi.queueStats(signal),
    refetchInterval: 60_000,
  });

  const reset = () => {
    setCallId(undefined);
    setNote('');
    setSavedOutcome(undefined);
    setAiAction(undefined);
    setOverride(undefined);
    setTarget(undefined);
    setObjectChoices({});
    setAppointmentFor(undefined);
  };

  const next = useMutation({
    mutationFn: (skipOrBroker?: string | { skip?: string; brokerId?: string }) => {
      const skip = typeof skipOrBroker === 'string' ? skipOrBroker : skipOrBroker?.skip;
      const brokerId = typeof skipOrBroker === 'object' && skipOrBroker !== null && 'brokerId' in skipOrBroker ? skipOrBroker.brokerId : selectedBrokerId;
      return papi.queueNext({
        release_current: true,
        skip_lead_id: skip,
        field_sales_user_id: brokerId || undefined,
      });
    },
    onSuccess: (res) => {
      reset();
      void queryClient.invalidateQueries({ queryKey: projectKeys.queueStats(project.id) });
      void queryClient.invalidateQueries({ queryKey: ['projects', project.id, 'queue-peek'] });
      if (!res.lead) {
        setLeadId(undefined);
        setPhase('idle');
        setEmpty({ reason: res.reason, next: res.next_window_at });
        return;
      }
      const id = res.lead.lead.id;
      queryClient.setQueryData(projectKeys.focus(project.id, id), res.lead);
      setEmpty(undefined);
      setTier(res.tier);
      setLeadId(id);
      setPhase('focus');
      // Vorladen: Historie des voraussichtlich nächsten Leads.
      void papi
        .queuePeek(id)
        .then((peek) => {
          const upcoming = peek.items[0]?.lead_id;
          if (upcoming) void queryClient.prefetchQuery({ queryKey: projectKeys.timeline(project.id, upcoming), queryFn: () => papi.activities(upcoming) });
        })
        .catch(() => undefined);
    },
  });

  // Laufenden Anruf nach Neuladen fortsetzen; sonst (von der Startseite) direkt den nächsten Lead holen.
  const autoStarted = useRef(false);
  const startNext = useEffectEvent(() => next.mutate(undefined));
  useEffect(() => {
    let cancelled = false;
    papi
      .activeCall()
      .then(({ call: c }) => {
        if (cancelled) return;
        if (c?.lead_id) {
          setLeadId(c.lead_id);
          setCallId(c.id);
          setPhase('calling');
        } else if (autoStart && !autoStarted.current) {
          autoStarted.current = true;
          startNext();
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [papi, autoStart]);

  // Browser-Telefonie: Headset im Browser statt eigenem Telefon (Standard, sofern eingerichtet); das Telefon lebt
  // sitzungsweit (PhoneProvider), damit auch Rückrufe von Kunden überall klingeln.
  const phone = usePhone();
  const browserMode = phone.browserMode;
  const [help, setHelp] = useState(() => localStorage.getItem(helpKey) === '1');
  const toggleHelp = () => {
    localStorage.setItem(helpKey, help ? '0' : '1');
    setHelp(!help);
  };

  // Wen „Anruf starten“ anruft: Vorschlag des Servers (Hauptansprechpartner, nach erfolglosem Versuch die nächste Nummer)
  // oder die Auswahl des Mitarbeiters.
  const targetInfo = focus.data?.call_targets;
  const targets = targetInfo?.targets ?? [];
  const suggested = targetInfo ? targets[targetInfo.default] : undefined;
  const chosenPhone = target && target.lead === leadId ? target.phone : suggested?.phone;

  const start = useMutation({
    mutationFn: (o?: { reason: string }) => {
      if (browserMode) phone.expect();
      return papi.startCall({
        lead_id: leadId as string,
        ...(targets.length > 1 && chosenPhone ? { phone: chosenPhone } : {}),
        override_policy: o ? true : undefined,
        override_reason: o?.reason,
      });
    },
    onSuccess: (call) => {
      setOverride(undefined);
      setCallId(call.id);
      setPhase('calling');
      queryClient.setQueryData(projectKeys.call(project.id, call.id), call);
    },
    onError: (error) => {
      phone.cancelExpect();
      if (error instanceof ApiError && error.code === 'contact_policy_blocked') {
        setOverride({ reasons: error.fieldErrors.map((f) => ({ code: f.code, message: f.message, overridable: true })), text: '' });
      }
    },
  });

  // Anrufstatus: schnell abfragen, solange es klingelt. Im Gespräch meldet das Browser-Telefon das Auflegen selbst
  // (sofortige Abfrage unten), dann genügt alle 5 Sekunden – das spart rund zwei Drittel der Abfragen je Gespräch.
  const phoneInCall = browserMode && phone.state === 'in-call';
  const call = useQuery({
    queryKey: projectKeys.call(project.id, callId ?? ''),
    queryFn: ({ signal }) => papi.call(callId as string, signal),
    enabled: !!callId,
    refetchInterval: (q) =>
      q.state.data && terminalCallStatuses.includes(q.state.data.status) ? false : phoneInCall && q.state.data?.status === 'IN_PROGRESS' ? 5000 : 1500,
  });
  const wasInCall = useRef(false);
  useEffect(() => {
    if (phoneInCall) {
      wasInCall.current = true;
    } else if (wasInCall.current) {
      wasInCall.current = false;
      if (callId) void queryClient.invalidateQueries({ queryKey: projectKeys.call(project.id, callId) });
    }
  }, [phoneInCall, callId, project.id, queryClient]);
  const ended = !!call.data && terminalCallStatuses.includes(call.data.status);
  // Termin oder Rückruf im laufenden Gespräch eintragen: Status, Stumm und „Auflegen“ bleiben sichtbar (Thomas 15.10.2026:
  // „wenn ich beim Gespräch einen Termin ausmache, kann ich nicht mehr auflegen“).
  const callLive = !!callId && !!call.data && !ended;
  // Nach einem Gespräch ist das Ergebnis Pflicht (unbeantwortete Anrufe erfasst das System selbst als „Nicht erreicht“).
  const outcomeMissing = !!callId && !!call.data && !call.data.outcome && !savedOutcome && phase !== 'done';
  // Nach Gesprächsende automatisch in die Nachbearbeitung.
  const view: Phase = phase === 'calling' && ended ? 'wrapup' : phase;
  useEffect(() => {
    if (ended && leadId) void queryClient.invalidateQueries({ queryKey: projectKeys.focus(project.id, leadId) });
  }, [ended, leadId, project.id, queryClient]);

  // KI-Auswertung während der Nachbearbeitung abfragen (Vorbelegung, sobald verfügbar).
  const analysis = useQuery({
    queryKey: ['projects', project.id, 'analysis', callId],
    queryFn: ({ signal }) => papi.callAnalysis(callId as string, signal),
    enabled: !!callId && can('ai.review') && (view === 'wrapup' || view === 'done'),
    refetchInterval: (q) => (q.state.data?.analysis?.status === 'COMPLETED' ? false : 5000),
  });
  const aiOutcome = analysis.data?.analysis?.outcome as CallOutcomeCode | undefined;

  // Hinweis, wenn ein Ergebnis eine Notiz braucht (Klärung, Kaufgesuch).
  const [noteHint, setNoteHint] = useState('');
  const noteRef = useRef<HTMLInputElement>(null);
  const outcome = useMutation({
    mutationFn: (o: { outcome: CallOutcomeCode; followUp?: FollowUpSpec; objects?: CallOutcome['objects'] }) => {
      // Weitere Objekte des Eigentümers nur, wenn er erreicht wurde.
      const objects = o.objects ?? (reachedOutcomes.includes(o.outcome) ? objectsPayload(objectChoices) : []);
      return papi.setOutcome(callId as string, {
        outcome: o.outcome,
        note: note || undefined,
        follow_up: o.followUp,
        ...(objects.length > 0 ? { objects } : {}),
      });
    },
    onSuccess: (c) => {
      setSavedOutcome(c.outcome ?? undefined);
      setPhase('done');
      // Den Anruf liefert die Antwort selbst; neu laden, was das Ergebnis ändert (Lead, Chronik, Zähler, KI-Hinweise) –
      // nicht die Projekteinstellungen (vorher 9 Abfragen je Ergebnis, darunter Einstellungen und Anruf erneut).
      queryClient.setQueryData(projectKeys.call(project.id, c.id), c);
      void queryClient.invalidateQueries({
        queryKey: projectKeys.all(project.id),
        predicate: (q) => q.queryKey[2] !== 'settings' && !(q.queryKey[2] === 'call' && q.queryKey[3] === c.id),
      });
    },
  });
  // Gesprächsnotiz ohne (erneutes) Ergebnis an den Lead hängen – z. B. wenn „nicht erreicht“ automatisch erfasst wurde.
  const saveNote = useMutation({
    mutationFn: (text: string) => papi.appendLeadNote(leadId as string, text),
    onSuccess: () => {
      setNote('');
      if (leadId) void queryClient.invalidateQueries({ queryKey: projectKeys.focus(project.id, leadId) });
      if (leadId) void queryClient.invalidateQueries({ queryKey: projectKeys.timeline(project.id, leadId) });
    },
  });
  // Wiedervorlage ohne Anruf (z. B. Lead vorab verschieben).
  const schedule = useMutation({
    mutationFn: (spec: FollowUpSpec) => papi.createFollowUp({ lead_id: leadId as string, type: 'FOLLOW_UP', ...spec }),
    onSuccess: () => {
      setSavedOutcome('FOLLOW_UP');
      setPhase('done');
      void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
    },
  });
  const endCall = useMutation({
    mutationFn: () => {
      phone.hangup();
      return papi.endCall(callId as string);
    },
  });
  // Aufzeichnung erst nach Zustimmung des Kunden: Der Telefonist informiert im Gespräch und startet dann.
  const settings = useQuery({ queryKey: projectKeys.settings(project.id), queryFn: ({ signal }) => papi.settings(signal), staleTime: 5 * 60_000 });
  const recording = useMutation({
    mutationFn: (start: boolean) => (start ? papi.startRecording(callId as string) : papi.stopRecording(callId as string)),
    onSuccess: (c) => queryClient.setQueryData(projectKeys.call(project.id, c.id), (prev: object | undefined) => ({ ...prev, ...c })),
  });

  // Reservierung während der Bearbeitung verlängern.
  useEffect(() => {
    if (!leadId || phase === 'idle') return;
    const timer = setInterval(() => void papi.lockLead(leadId).catch(() => undefined), LOCK_HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [leadId, phase, papi]);

  const pick = (o: CallOutcomeCode) => {
    const hint = noteRequired[o];
    if (hint && !note.trim()) {
      setNoteHint(hint);
      noteRef.current?.focus();
      return;
    }
    setNoteHint('');
    if (o === 'APPOINTMENT') setPhase('appointment');
    else if (o === 'CALLBACK' || o === 'FOLLOW_UP') setPhase('followup');
    else if (callId) outcome.mutate({ outcome: o });
  };

  // Tastatur: C = Anruf, R = Rückruf, T = Termin, N = nächster Lead.
  const onKey = useEffectEvent((e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
    const k = e.key.toLowerCase();
    const allowed = focus.data?.policy.allowed ?? false;
    if (k === 'c' && view === 'focus' && allowed && !start.isPending && (!browserMode || phone.state === 'ready')) start.mutate(undefined);
    else if (k === 'n' && (view === 'idle' || view === 'done' || (view === 'focus' && !callId)) && !outcomeMissing && !next.isPending) next.mutate(undefined);
    else if (k === 'r' && callId && (view === 'calling' || view === 'wrapup')) setPhase('followup');
    else if (k === 't' && leadId && (view === 'focus' || view === 'calling' || view === 'wrapup')) setPhase('appointment');
    else return;
    e.preventDefault();
  });
  useEffect(() => {
    const listener = (e: KeyboardEvent) => onKey(e);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  const f = focus.data;
  // Anstehender Termin: In der Akquise steht dann „Termin bestätigen“ mit Anruf ohne Akquise-Sperren vorn.
  const upcoming = f?.appointments
    .filter((a) => activeAppointmentStatuses.includes(a.status) && Date.parse(a.start_at) > openedAt)
    .sort((x, y) => x.start_at.localeCompare(y.start_at))[0];
  const appointmentDefaults: AppointmentDefaults | undefined = f
    ? {
        startAt: typeof aiAction?.payload.start_at === 'string' ? aiAction.payload.start_at : undefined,
        street: f.lead.street,
        houseNumber: f.lead.house_number,
        postalCode: f.lead.postal_code,
        city: f.lead.city,
        contactName: [f.lead.first_name, f.lead.last_name].filter(Boolean).join(' ') || null,
        contactPhone: f.lead.phone,
        notes: note,
        email: f.lead.email,
      }
    : undefined;

  const brokers = stats.data?.brokers ?? [];
  const selectedBroker = brokers.find((b) => b.user_id === selectedBrokerId);
  const fullBrokers = brokers.filter((b) => b.calendar_full);

  return (
    <div className="page workspace">
      <header className="page-header workspace-header">
        <h1>Akquise</h1>
        {stats.data && (
          <span className="muted">
            {stats.data.callbacks_due} Rückrufe fällig ·{' '}
            <Link to={`/p/${project.key}/akquise?ansicht=wiedervorlagen`}>{stats.data.follow_ups_due} Wiedervorlagen</Link> · {stats.data.new_leads}
            {stats.data.capped ? '+' : ''} neue Leads
            {stats.data.new_leads_field_sales > 0 && <> (davon {stats.data.new_leads_field_sales} in Gebieten mit Makler)</>}
          </span>
        )}
        <div className="topbar-spacer" />
        {brokers.length > 0 && (
          <div className="inline-controls" style={{ flexWrap: 'wrap' }}>
            <label className="inline-controls" style={{ fontWeight: 600, fontSize: '0.9rem' }}>
              <span>Telefonieren für:</span>
              <select
                value={selectedBrokerId}
                disabled={view === 'calling' || next.isPending}
                onChange={(e) => {
                  const nextId = e.target.value;
                  setSelectedBrokerId(nextId);
                  if (view !== 'calling' && !outcomeMissing) {
                    next.mutate({ brokerId: nextId });
                  }
                }}
                style={{ minHeight: '38px', padding: '0 0.65rem', borderRadius: '6px', border: '1px solid var(--color-border-strong)' }}
              >
                <option value="">Automatisch (freie Kalender zuerst)</option>
                {brokers.map((b) => (
                  <option key={b.user_id} value={b.user_id}>
                    {b.name} · {b.upcoming_appointments}/{b.max_recommended} Termine (14 Tg.)
                    {b.calendar_full ? ' [KALENDER VOLL]' : ' [FREI]'}
                  </option>
                ))}
              </select>
            </label>
            <Link
              className="btn btn-small"
              to={selectedBrokerId ? `/p/${project.key}/termine?makler=${encodeURIComponent(selectedBrokerId)}` : `/p/${project.key}/termine`}
            >
              Kalender ansehen
            </Link>
          </div>
        )}
        <button type="button" className="btn btn-ghost" aria-pressed={help} onClick={toggleHelp}>
          Hilfe
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={next.isPending || view === 'calling' || outcomeMissing}
          title={outcomeMissing ? 'Bitte zuerst das Gesprächsergebnis wählen.' : undefined}
          onClick={() => next.mutate(undefined)}
        >
          Nächster Lead <kbd>N</kbd>
        </button>
      </header>
      {selectedBroker?.calendar_full && (
        <p className="alert alert-warn" role="status">
          <strong>Kalender-Hinweis ({selectedBroker.name}):</strong> Im Kalender stehen bereits{' '}
          <strong>{selectedBroker.upcoming_appointments} Termine</strong> in den nächsten 14 Tagen (Empfehlung max. {selectedBroker.max_recommended}). Der
          Kalender ist voll – bitte vorrangig für Makler mit freier Kapazität telefonieren oder vorher im{' '}
          <Link to={`/p/${project.key}/termine?makler=${encodeURIComponent(selectedBroker.user_id)}`}>Kalender prüfen</Link>.
        </p>
      )}
      {!selectedBroker && fullBrokers.length > 0 && brokers.some((b) => !b.calendar_full) && (
        <p className="alert alert-info" role="status">
          <strong>Automatische Kalender-Steuerung aktiv:</strong> Da der Kalender von{' '}
          <strong>{fullBrokers.map((b) => `${b.name} (${b.upcoming_appointments} Termine)`).join(', ')}</strong> für die nächsten 14 Tage bereits voll ist,
          schlägt das System automatisch vorrangig Kontakte für{' '}
          <strong>
            {brokers
              .filter((b) => !b.calendar_full)
              .map((b) => `${b.name} (${b.upcoming_appointments}/${b.max_recommended} Termine)`)
              .join(', ')}
          </strong>{' '}
          vor. Über das Auswahlfeld oben können Sie jederzeit manuell umschalten.
        </p>
      )}
      {next.isError && <MutationError error={next.error} />}
      {!leadId ? (
        <>
          {empty?.reason === 'OUTSIDE_CALL_HOURS' && (
            <p className="alert alert-warn">Außerhalb der Anrufzeiten – nächstes Zeitfenster: {fmtDateTime(empty.next)}.</p>
          )}
          {empty?.reason === 'QUEUE_EMPTY' && (
            <p className="alert alert-info">Gerade ist kein Lead fällig – neue Rückrufe und Wiedervorlagen erscheinen automatisch.</p>
          )}
          <NextCallCard onStart={() => next.mutate(undefined)} starting={next.isPending} />
        </>
      ) : focus.isPending ? (
        <PageLoader label="Lead wird geladen …" />
      ) : focus.isError ? (
        <ErrorMessage error={focus.error} onRetry={() => void focus.refetch()} />
      ) : (
        f && (
          <div className="workspace-grid">
            <div className="workspace-main">
              {help && <WorkflowSteps steps={workflowSteps} current={workflowIndex(view, !!callId)} />}
              <LeadCard focus={f} tier={tier} />
              <BuyerMatchHint leadId={f.lead.id} />
              <section className="card call-panel" aria-label="Anruf">
                {view === 'focus' && (
                  <>
                    {upcoming && <UpcomingConfirmation appointmentId={upcoming.id} leadId={f.lead.id} leadName={personName(f.lead)} />}
                    {!f.policy.allowed && !upcoming && (
                      <div className="alert alert-warn">
                        <strong>Anruf derzeit nicht vorgesehen</strong>
                        <ul className="plain-list">
                          {f.policy.blocking.map((r) => (
                            <li key={r.code}>{r.message}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {f.policy.warnings.map((w) => (
                      <p key={w.code} className="alert alert-info">
                        {w.message}
                      </p>
                    ))}
                    {browserMode && <BrowserPhoneLine state={phone.state} error={phone.error} />}
                    {(targetInfo?.suppressed ?? 0) > 0 && (
                      <p className="alert alert-warn">
                        {targetInfo?.suppressed === 1 ? 'Eine Nummer steht' : `${targetInfo?.suppressed} Nummern stehen`} auf der Sperrliste (Kontaktstopp bzw.
                        Löschung über diesen Anschluss) und werden nicht angerufen.
                      </p>
                    )}
                    {targets.length > 1 && (
                      <div className="call-target">
                        <label className="field">
                          <span>Anrufen</span>
                          <select value={chosenPhone} onChange={(e) => setTarget({ lead: f.lead.id, phone: e.target.value })}>
                            {targets.map((t) => (
                              <option key={t.phone} value={t.phone}>
                                {t.name} ({roleText[t.role] ?? t.role}) · {t.display}
                              </option>
                            ))}
                          </select>
                        </label>
                        {targetInfo?.reason && chosenPhone === suggested?.phone && <p className="muted small">{targetInfo.reason}</p>}
                      </div>
                    )}
                    <button
                      type="button"
                      className={upcoming ? 'btn' : 'btn btn-primary btn-call'}
                      disabled={start.isPending || (!f.policy.allowed && !f.policy.can_override) || !f.lead.phone || (browserMode && phone.state !== 'ready')}
                      onClick={() => (f.policy.allowed ? start.mutate(undefined) : setOverride({ reasons: f.policy.blocking, text: '' }))}
                    >
                      {upcoming ? (
                        'Akquise-Anruf (anderes Anliegen)'
                      ) : (
                        <>
                          ANRUF STARTEN <kbd>C</kbd>
                        </>
                      )}
                    </button>
                    <div className="secondary-actions">
                      <button type="button" className="btn" onClick={() => setPhase('appointment')}>
                        Termin eintragen <kbd>T</kbd>
                      </button>
                      <button type="button" className="btn" onClick={() => setPhase('followup')}>
                        Wiedervorlage setzen
                      </button>
                      <button type="button" className="btn btn-ghost" disabled={next.isPending} onClick={() => next.mutate(leadId)}>
                        Überspringen
                      </button>
                    </div>
                    {override && (
                      <form
                        className="alert alert-warn"
                        onSubmit={(e) => {
                          e.preventDefault();
                          start.mutate({ reason: override.text });
                        }}
                      >
                        <strong>Kontaktregel übersteuern</strong>
                        <ul className="plain-list">
                          {override.reasons.map((r) => (
                            <li key={r.code}>{r.message}</li>
                          ))}
                        </ul>
                        {can('call.override_policy') ? (
                          <label className="field wide">
                            <span>Begründung (wird protokolliert)</span>
                            <input value={override.text} onChange={(e) => setOverride({ ...override, text: e.target.value })} required />
                            <button type="submit" className="btn" disabled={start.isPending || !override.text.trim()}>
                              Trotzdem anrufen
                            </button>
                          </label>
                        ) : (
                          <p className="muted">Eine Ausnahme kann nur die Teamleitung freigeben.</p>
                        )}
                      </form>
                    )}
                    {start.isError && !override && <MutationError error={start.error} />}
                  </>
                )}
                {(view === 'calling' || view === 'wrapup' || callLive) && call.data && (
                  <CallStatusBar
                    status={call.data.status}
                    startedAt={call.data.answered_at ?? call.data.started_at}
                    endedAt={call.data.ended_at}
                    failure={call.data.failure_reason}
                    onEnd={callLive ? () => endCall.mutate() : undefined}
                    muted={callLive && phone.state === 'in-call' ? phone.muted : undefined}
                    onMute={phone.toggleMute}
                    browser={browserMode}
                    inbound={call.data.direction === 'INBOUND'}
                  />
                )}
                {view === 'calling' &&
                  call.data &&
                  call.data.status === 'IN_PROGRESS' &&
                  (settings.data?.recording.enabled || call.data.agent_recording_started_at) && (
                    <RecordingControl
                      agentTrack={!!call.data.agent_recording_started_at}
                      consentPossible={!!settings.data?.recording.enabled}
                      running={!!call.data.recording_consent_at && !call.data.recording_stopped_at}
                      stopped={!!call.data.recording_stopped_at}
                      busy={recording.isPending}
                      onStart={() => recording.mutate(true)}
                      onStop={() => recording.mutate(false)}
                    />
                  )}
                {recording.isError && <MutationError error={recording.error} />}
                {view === 'calling' &&
                  call.data?.status === 'IN_PROGRESS' &&
                  call.data.direction === 'OUTBOUND' &&
                  call.data.recording_consent_at &&
                  !call.data.recording_stopped_at && (
                  <LiveAssistPanel callId={call.data.id} active={!!call.data.live_assist_active} />
                )}
                {(view === 'calling' || view === 'wrapup') && (
                  <>
                    {view === 'wrapup' && call.data?.outcome_source === 'SYSTEM' && call.data.outcome && (
                      <p className="alert alert-info">
                        Automatisch erfasst: {outcomeLabels[call.data.outcome]} – nächster Versuch wird eingeplant. Sie können das Ergebnis ändern.
                      </p>
                    )}
                    {aiOutcome && (
                      <p className="alert alert-info">
                        KI-Vorschlag: <strong>{outcomeLabels[aiOutcome]}</strong>
                        {analysis.data?.analysis?.summary && <> – {analysis.data.analysis.summary}</>}
                        {call.data?.recording?.track === 'AGENT' && (
                          <>
                            <br />
                            <small>Nur aus Ihrer Gesprächsseite abgeleitet – bitte prüfen. Übernommen wird nichts automatisch.</small>
                          </>
                        )}
                      </p>
                    )}
                    {outcomeMissing && view === 'wrapup' && <p className="muted small">Bitte das Gesprächsergebnis wählen – danach geht es zum nächsten Lead.</p>}
                    <div className="outcome-grid" role="group" aria-label="Gesprächsergebnis">
                      {quickOutcomes.map((q) => (
                        <button
                          key={q.outcome}
                          type="button"
                          className={aiOutcome === q.outcome ? 'btn outcome suggested' : 'btn outcome'}
                          disabled={outcome.isPending || !callId}
                          onClick={() => pick(q.outcome)}
                        >
                          {q.label} {q.key && <kbd>{q.key}</kbd>}
                        </button>
                      ))}
                    </div>
                    <label className="field wide">
                      <span>Notiz zum Gespräch (optional, wird mit dem Ergebnis gespeichert)</span>
                      <input
                        ref={noteRef}
                        value={note}
                        maxLength={4000}
                        onChange={(e) => {
                          setNote(e.target.value);
                          if (noteHint && e.target.value.trim()) setNoteHint('');
                        }}
                        placeholder="Kurz das Wichtigste"
                        aria-invalid={!!noteHint}
                      />
                      {noteHint && <small className="field-error">{noteHint}</small>}
                    </label>
                    {/* Ergebnis schon automatisch erfasst (nicht erreicht): die Notiz sonst nicht verlieren – an den Lead hängen. */}
                    {view === 'wrapup' && call.data?.outcome && note.trim() && (
                      <div className="form-actions">
                        <button type="button" className="btn btn-small" disabled={saveNote.isPending} onClick={() => saveNote.mutate(note.trim())}>
                          Gesprächsnotiz speichern
                        </button>
                        {saveNote.isError && <MutationError error={saveNote.error} />}
                      </div>
                    )}
                    {callId && (
                      <OwnerObjectOutcomes
                        leadId={f.lead.id}
                        choices={objectChoices}
                        onChange={(id, c) => setObjectChoices((prev) => ({ ...prev, [id]: c }))}
                        onAppointment={setAppointmentFor}
                        onNotInterestedAll={(objs) =>
                          outcome.mutate({ outcome: 'NOT_INTERESTED', objects: objs.map((x) => ({ lead_id: x.id, outcome: 'NOT_INTERESTED' })) })
                        }
                        busy={outcome.isPending}
                      />
                    )}
                    {callId && <ContactStop key={f.lead.id} leadId={f.lead.id} />}
                    {outcome.isError && <MutationError error={outcome.error} />}
                  </>
                )}
                {view === 'followup' && (
                  <div className="followup-step">
                    <h2>{callId ? 'Rückruf vereinbaren' : 'Wiedervorlage setzen'}</h2>
                    <FollowUpPicker
                      busy={outcome.isPending || schedule.isPending}
                      onPick={(spec) => (callId ? outcome.mutate({ outcome: 'CALLBACK', followUp: spec }) : schedule.mutate(spec))}
                    />
                    {(outcome.isError || schedule.isError) && <MutationError error={outcome.error ?? schedule.error} />}
                    <button type="button" className="btn btn-ghost" onClick={() => setPhase(callId ? 'wrapup' : 'focus')}>
                      Zurück
                    </button>
                  </div>
                )}
                {view === 'done' && (
                  <div className="done-step" role="status">
                    {savedOutcome === 'APPOINTMENT' ? (
                      <AppointmentSuccess />
                    ) : (
                      <p className="alert alert-success">
                        Gespeichert{savedOutcome ? `: ${outcomeLabels[savedOutcome as CallOutcomeCode] ?? savedOutcome}` : ''}.
                      </p>
                    )}
                    <button type="button" className="btn btn-primary" disabled={next.isPending} onClick={() => next.mutate(undefined)} autoFocus>
                      Nächster Lead <kbd>N</kbd>
                    </button>
                    <NextCallHint excludeLeadId={leadId} />
                    <AutoNext onNext={() => next.mutate(undefined)} busy={next.isPending} />
                  </div>
                )}
                {help && <Shortcuts keys={workflowShortcuts(view, !!callId)} />}
              </section>
              {view === 'appointment' && (
                <AppointmentForm
                  leadId={f.lead.id}
                  sourceCallId={callId}
                  defaults={appointmentDefaults}
                  onCancel={() => setPhase(callId ? 'wrapup' : 'focus')}
                  onSaved={(appt) => {
                    if (aiAction) void papi.acceptAction(aiAction.id, appt.id).catch(() => undefined);
                    // Ergebnisse für weitere Objekte des Eigentümers mit dem Termin festhalten.
                    const objects = objectsPayload(objectChoices);
                    if (callId && objects.length > 0) {
                      outcome.mutate({ outcome: 'APPOINTMENT', objects });
                      return;
                    }
                    setSavedOutcome('APPOINTMENT');
                    setPhase('done');
                  }}
                />
              )}
              {appointmentFor && (
                <section className="card" aria-label="Termin für weiteres Objekt">
                  <h2>Termin für {[appointmentFor.street, appointmentFor.house_number].filter(Boolean).join(' ') || 'weiteres Objekt'}</h2>
                  <AppointmentForm
                    leadId={appointmentFor.id}
                    sourceCallId={callId}
                    defaults={{
                      street: appointmentFor.street,
                      houseNumber: appointmentFor.house_number,
                      postalCode: appointmentFor.postal_code,
                      city: appointmentFor.city,
                      contactName: appointmentDefaults?.contactName ?? null,
                      contactPhone: appointmentDefaults?.contactPhone ?? null,
                      email: appointmentDefaults?.email ?? null,
                    }}
                    onCancel={() => setAppointmentFor(undefined)}
                    onSaved={() => {
                      setObjectChoices((prev) => ({ ...prev, [appointmentFor.id]: 'APPOINTMENT_DONE' }));
                      setAppointmentFor(undefined);
                    }}
                  />
                </section>
              )}
              <LeadDetails focus={f} tier={tier} />
            </div>
            <aside className="workspace-side">
              <AIReviewPanel
                leadId={f.lead.id}
                compact
                onCreateAppointment={(a) => {
                  setAiAction(a);
                  setPhase('appointment');
                }}
              />
              <ConversationInsights leadId={f.lead.id} collapsed />
              <section className="card">
                <h2>Historie</h2>
                <LeadTimeline leadId={f.lead.id} limit={5} />
              </section>
            </aside>
          </div>
        )
      )}
    </div>
  );
}

const phoneStateText: Record<BrowserPhoneState, string> = {
  off: 'Browser-Telefon aus',
  starting: 'Browser-Telefon wird verbunden …',
  ready: 'Browser-Telefon bereit (Headset)',
  ringing: 'Verbindung wird aufgebaut …',
  incoming: 'Eingehender Anruf …',
  'in-call': 'Im Gespräch über den Browser',
  error: 'Browser-Telefon nicht bereit',
};

function BrowserPhoneLine({ state, error }: { state: BrowserPhoneState; error?: string }) {
  return (
    <p className={`browser-phone browser-phone-${state}`} role="status" aria-live="polite">
      <span className="call-dot" aria-hidden="true" />
      {phoneStateText[state]}
      {state === 'error' && error && <span className="muted"> – {error}</span>}
    </p>
  );
}

// RecordingControl: Die Stimme des Telefonisten wird (je nach Projekt) automatisch aufgenommen; die vollständige
// Aufnahme mit KI-Mitschrift beider Seiten startet der Telefonist per Knopf erst nach Zustimmung des Kunden (auch bei
// Rückrufen von Kunden) und beendet sie auf dessen Wunsch.
function RecordingControl({
  agentTrack,
  consentPossible,
  running,
  stopped,
  busy,
  onStart,
  onStop,
}: {
  agentTrack: boolean;
  consentPossible: boolean;
  running: boolean;
  stopped: boolean;
  busy: boolean;
  onStart: () => void;
  onStop: () => void;
}) {
  if (running)
    return (
      <div className="recording-control recording-on" role="status">
        <span className="recording-dot" aria-hidden="true" />
        <strong>Aufzeichnung und KI-Mitschrift laufen (beide Seiten)</strong>
        <button type="button" className="btn btn-small" disabled={busy} onClick={onStop}>
          Beenden
        </button>
      </div>
    );
  return (
    <div className="recording-control">
      {agentTrack && (
        <span className="recording-agent" role="status">
          <span className="recording-dot recording-dot-agent" aria-hidden="true" />
          Ihre Seite wird aufgezeichnet
        </span>
      )}
      {stopped ? (
        <span className="muted">Vollständige Aufnahme auf Wunsch des Kunden beendet.</span>
      ) : (
        consentPossible && (
          <div className="recording-consent">
            <button
              type="button"
              className="btn btn-record"
              disabled={busy}
              onClick={onStart}
              title="Nur mit Zustimmung – erst fragen: „Darf ich das Gespräch aufzeichnen? Unsere Software schreibt dann mit, damit nichts verloren geht.“ Sagt der Kunde ja, klicken."
            >
              <span className="recording-dot" aria-hidden="true" />
              Aufzeichnen und mitschreiben
            </button>
          </div>
        )
      )}
    </div>
  );
}


function LeadCard({ focus: f, tier }: { focus: LeadFocus; tier?: string }) {
  const statusLabels = useLeadStatusLabels();
  const l = f.lead;
  const contact = [l.salutation, l.first_name, l.last_name].filter(Boolean).join(' ');
  // Ohne Straße bzw. Hausnummer lassen sich Termin, Bewertung und Abgleich nicht sauber machen – im Gespräch erfragen.
  const noStreet = !l.street?.trim();
  const noNumber = !noStreet && !l.house_number?.trim() && !/\d/.test(l.street ?? '');
  return (
    <section className="card lead-card" aria-labelledby="lead-title">
      <div className="lead-card-head">
        <div>
          {tier && <p className="eyebrow">{tierLabels[tier] ?? tier}</p>}
          <h2 id="lead-title">{personName(l)}</h2>
          {contact && l.company_name && <p className="lead-contact">{contact}</p>}
        </div>
        <div className="badges">
          <span className="badge badge-muted">{statusLabels[l.status] ?? l.status}</span>
          {l.geo_status !== 'VALID' && <GeoStatusBadge status={l.geo_status} />}
          {f.contact_state === 'OVERCONTACTED' && <span className="badge badge-error">Cooldown</span>}
          {f.pending_ai_reviews > 0 && <span className="badge badge-warn">{f.pending_ai_reviews} KI-Vorschläge</span>}
        </div>
      </div>
      <div className="lead-phones">
        {l.phone && (
          <a className="phone" href={`tel:${l.phone}`}>
            <Icon name="phone" /> {l.phone}
          </a>
        )}
        {l.mobile_phone && (
          <a className="phone" href={`tel:${l.mobile_phone}`}>
            <Icon name="smartphone" /> {l.mobile_phone}
          </a>
        )}
        {l.email && <a href={`mailto:${l.email}`}>{l.email}</a>}
      </div>
      <LeadContactEdit lead={l} />
      {/* Fälliger Rückruf bzw. fällige Wiedervorlage: der Anlass (z. B. „Termin absagen: …“ mit Gesprächshilfe) steht oben. */}
      {purposeShown(f, tier) && f.open_follow_up && (
        <div className="call-purpose" role="note" aria-label="Anlass des Anrufs">
          <Icon name="phone" size={18} />
          <div>
            <strong>{f.open_follow_up.type === 'CALLBACK' ? 'Anlass des Rückrufs' : 'Anlass der Wiedervorlage'}</strong>
            <p>{f.open_follow_up.reason}</p>
          </div>
        </div>
      )}
      <CallObjectFacts lead={l} />
      {(noStreet || noNumber) && (
        <p className="alert alert-warn">
          {noStreet ? 'Straße und Hausnummer fehlen' : 'Hausnummer fehlt'} – bitte im Gespräch erfragen und eintragen (nötig für Termin, Bewertung und
          Zuordnung).
        </p>
      )}
      <AddressConfirm key={l.id} lead={l} />
      <LeadObjectsPanel leadId={l.id} compact />
      <SameNumberLeads items={f.same_number ?? []} compact />
      <NotePreview notes={l.notes} />
    </section>
  );
}

const purposeTiers = ['CALLBACK_DUE', 'FOLLOW_UP_DUE', 'FOLLOW_UP_TODAY'];

function purposeShown(f: LeadFocus, tier?: string): boolean {
  return !!f.open_follow_up?.reason && !!tier && purposeTiers.includes(tier);
}

// LeadDetails: Hintergrund unter den Anrufknöpfen – Zuständigkeit, bisherige Versuche, Eigentümer und alle Notizen.
function LeadDetails({ focus: f, tier }: { focus: LeadFocus; tier?: string }) {
  const { project } = useProject();
  const l = f.lead;
  return (
    <section className="card lead-details" aria-label="Weitere Angaben zum Lead">
      <dl className="facts lead-facts">
        <dt>Gebiet</dt>
        <dd>
          {f.area_name ?? '–'}
          {f.county_name && <span className="muted"> · Landkreis {f.county_name}</span>}
        </dd>
        <dt>Außendienst</dt>
        <dd>
          {f.field_sales_name ?? '–'}
          {f.field_sales_suggested && <span className="muted"> (laut Gebiet)</span>}
        </dd>
        <dt>Kontaktversuche</dt>
        <dd>
          {f.contact_attempts}
          {f.last_call_at && <span className="muted"> · zuletzt {fmtDateTime(f.last_call_at)}</span>}
        </dd>
        {f.open_follow_up && !purposeShown(f, tier) && (
          <>
            <dt>Wiedervorlage</dt>
            <dd>{`${fmtDateTime(f.open_follow_up.due_at)}${f.open_follow_up.reason ? ` – ${f.open_follow_up.reason}` : ''}`}</dd>
          </>
        )}
        {f.appointments.length > 0 && (
          <>
            <dt>Termine</dt>
            <dd>
              {f.appointments.slice(0, 2).map((a) => (
                <span key={a.id} className="block">
                  <Link to={`/p/${project.key}/termine/${a.id}`}>{fmtDateTime(a.start_at)}</Link>{' '}
                  <span className="muted">
                    {appointmentStatusLabels[a.status as keyof typeof appointmentStatusLabels] ?? a.status}
                    {a.field_sales_name && ` · ${a.field_sales_name}`}
                  </span>
                </span>
              ))}
            </dd>
          </>
        )}
      </dl>
      <OwnersPanel leadId={l.id} compact />
      <div id="notizen">
        <LeadNotes leadId={l.id} notes={l.notes} />
      </div>
    </section>
  );
}
